/**
 * `ProtectedLayout` Component
 *
 * This component serves as a protected layout wrapper for authenticated users.
 * It ensures that only logged-in users can access the application's main content.
 *
 * - If the user is not logged in, they are redirected to the login page.
 * - If the user's code is `"PW01"`, they are redirected to the change-password page.
 * - Manages sidebar state for navigation.
 * - Displays the franchise name, fetched from session storage.
 *
 * @returns {JSX.Element} The protected layout structure with sidebar, header, and dynamic content.
 */
import React, { createContext, useContext, useState } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import ProfileMenu from "../component/ProfileMenu"
import Sidebar from "../component/Sidebar"
import { AppSessionService } from "../../infrastructure/services/session"
import { TYPES } from "../models/types"
import HamburgerButton from "../component/button/HamburgerButton"

// Create Context for Search
const SearchContext = createContext<{
    searchQuery: string
    setSearchQuery: (query: string) => void
}>({
    searchQuery: "",
    setSearchQuery: () => { },
})
export const useSearch = () => useContext(SearchContext)

const SideBarLayout: React.FC = () => {
    const { isLoggedIn, userCode } = useAuth()
    const sessionService = new AppSessionService()
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const location = useLocation()
    const [searchQuery, setSearchQuery] = useState("")
    const user = sessionService.user

    return (
        <SearchContext.Provider value={{ searchQuery, setSearchQuery }}>
            <div className="flex flex-col md:flex-row min-h-screen bg-gray-200">
                {/* Sidebar */}
                <Sidebar
                    isSidebarOpen={isSidebarOpen}
                    toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                />

                {/* Overlay for small screens */}
                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                        onClick={() => setIsSidebarOpen(false)}
                    ></div>
                )}

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="p-2 sm:p-4 bg-gray-200 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-4 shadow-md">
                        <div className="flex items-center justify-between w-full sm:w-auto">
                            <div className="flex items-center">
                                <HamburgerButton
                                    data-automation-id="protectedlayout-hamburger-sidebar-button"
                                    id="protectedlayout-hamburger-sidebar-button"
                                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                    isOpen={isSidebarOpen}
                                />
                                <h1
                                    data-automation-id="protectedlayout-franchisename-text"
                                    id="protectedlayout-franchisename-text"
                                    className="text-sm sm:text-xl md:text-2xl font-bold whitespace-nowrap"
                                >
                                </h1>
                            </div>
                            <div className="sm:hidden">
                                <ProfileMenu />
                            </div>
                        </div>
                        <div className="w-full sm:flex-1 sm:mx-4 sm:mt-4">
                            <input
                                data-automation-id="protectedlayout-inputbox-searchbar-input"
                                id="protectedlayout-inputbox-searchbar-input"
                                type={TYPES.TEXT}
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="px-4 py-2 border rounded-full w-11/12 focus:outline-none bg-gray-100"
                            />
                        </div>
                        <div className="hidden sm:block flex-shrink-0">
                            <ProfileMenu />
                        </div>
                    </div>

                    {/* Outlet (Dynamic Content) */}
                    <div className="flex-1 overflow-y-auto px-2 sm:px-4 pt-0 pb-2 sm:pb-4 bg-gray-200">
                        <Outlet />
                    </div>
                </div>
            </div>
        </SearchContext.Provider>
    )
}

export default SideBarLayout